import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useGroupsStore } from "../../../store/admin/groupsStore";
import { useUserStore } from "../../../store/userStore";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import { isPlatformAdminUser } from "../../../hooks/useIsReseller";
import type { VehicleSocket } from "../../../store/admin/vehiclesSocketStore";

const DETAIL_LIMIT = 20;

/**
 * Cuántas unidades entran en el censo de nombre y estado.
 *
 * `units` sólo lleva las 20 que piden atención, ordenadas por urgencia, y con una
 * flota grande eso deja fuera categorías enteras: con 13 sin reportar y 7 en
 * ralentí no cabe una sola detenida, aunque haya 130. Y el movimiento en vivo no
 * lo tiene ninguna herramienta —`fleet_list_vehicles` es el padrón, no la
 * posición—, así que lo que no viaje aquí no se puede nombrar de ninguna otra
 * forma.
 *
 * El censo cuesta unos 9 tokens por unidad, así que 300 son ~2.700 por turno:
 * caro de mandar en el prompt cacheado, barato al lado de no poder responder.
 */
const ROSTER_LIMIT = 300;
const MAX_TEXT_LENGTH = 80;

export const FLEET_MOVEMENTS = ["driving", "ralenti", "stopped", "offline"] as const;

export type FleetMovement = (typeof FLEET_MOVEMENTS)[number];

export interface FleetSnapshotUnit {
  id: string;
  name: string;
  plate: string | null;
  movement: FleetMovement;
  speed_kph: number | null;
  place: string | null;
  minutes_since_report: number | null;
}

/** Una unidad en el censo: nombre y estado, con claves cortas porque van 300. */
export interface FleetSnapshotRosterUnit {
  n: string;
  m: FleetMovement;
}

export interface FleetSnapshot {
  captured_at: string;
  totals: Record<"units" | FleetMovement, number>;
  units: FleetSnapshotUnit[];
  roster: FleetSnapshotRosterUnit[];
  detail_truncated: boolean;
  roster_truncated: boolean;
}

const flatten = (value?: string | null): string | null => {
  if (!value) return null;
  const flat = value
    .replace(/[\r\n\t]+/g, " ")
    .replace(/[<>]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return flat.length > 0 ? flat.slice(0, MAX_TEXT_LENGTH) : null;
};

const movementOf = (vehicle: VehicleSocket): FleetMovement =>
  (FLEET_MOVEMENTS as readonly string[]).includes(vehicle.status)
    ? (vehicle.status as FleetMovement)
    : "offline";

const minutesSince = (seconds?: number): number | null => {
  if (!seconds || seconds <= 0) return null;
  return Math.max(0, Math.round((Date.now() - seconds * 1000) / 60000));
};

const scopedVehicles = (vehicles: VehicleSocket[]): VehicleSocket[] => {
  const isMonitor = localStorage.getItem("userRole") === "monitor";
  const isReseller = isPlatformAdminUser();
  const { selectedCompany, selectedGroup } = useVehiclesSocketStore.getState();
  const companyUser = useCompaniesStore.getState().companyUser;
  const groups = useGroupsStore.getState().groups;

  let result = vehicles;

  if (isMonitor) {
    const userId = localStorage.getItem("userId") || "";
    const groupDeviceIds = groups
      .filter((group) => group.monitorista_ids?.includes(userId))
      .flatMap((group) => group.devices_ids || []);
    const allowed = new Set([
      ...(useUserStore.getState().user?.available_devices || []),
      ...groupDeviceIds,
    ]);
    result = allowed.size > 0 ? result.filter((v) => allowed.has(v.id)) : [];
  }

  if (isReseller) {
    if (!selectedCompany) return [];
    result = result.filter((v) => v.companyId === selectedCompany);
  } else if (companyUser) {
    result = result.filter((v) => v.companyId === companyUser);
  }

  if (selectedGroup !== "all") {
    const groupDeviceIds = groups.find((g) => g.id === selectedGroup)?.devices_ids || [];
    result = result.filter((v) => groupDeviceIds.includes(v.id));
  }

  return result;
};

const ATTENTION_ORDER: Record<FleetMovement, number> = {
  offline: 0,
  ralenti: 1,
  driving: 2,
  stopped: 3,
};

const nameOf = (vehicle: VehicleSocket): string =>
  flatten(vehicle.vehicle?.name || vehicle.fullId || vehicle.id) ?? vehicle.id;

const describe = (vehicle: VehicleSocket): FleetSnapshotUnit => ({
  id: vehicle.id,
  name: nameOf(vehicle),
  plate: flatten(vehicle.vehicle?.plates),
  movement: movementOf(vehicle),
  speed_kph: vehicle.speed == null ? null : Math.round(vehicle.speed),
  place: flatten(vehicle.address),
  minutes_since_report: minutesSince(vehicle.lastReport?._seconds),
});

export function buildFleetSnapshot(): FleetSnapshot | null {
  const vehicles = scopedVehicles(useVehiclesSocketStore.getState().vehicles);
  if (vehicles.length === 0) return null;

  const totals = vehicles.reduce<Record<"units" | FleetMovement, number>>(
    (acc, vehicle) => ({ ...acc, [movementOf(vehicle)]: acc[movementOf(vehicle)] + 1 }),
    { units: vehicles.length, driving: 0, ralenti: 0, stopped: 0, offline: 0 },
  );

  const ranked = [...vehicles].sort((a, b) => {
    const byAttention = ATTENTION_ORDER[movementOf(a)] - ATTENTION_ORDER[movementOf(b)];
    return byAttention !== 0 ? byAttention : (b.speed ?? 0) - (a.speed ?? 0);
  });

  return {
    captured_at: new Date().toISOString(),
    totals,
    units: ranked.slice(0, DETAIL_LIMIT).map(describe),
    // Ordenado por nombre, no por urgencia: el censo se lee para encontrar una
    // unidad concreta o para listar las de un estado, y en las dos cosas el orden
    // alfabético ayuda. La urgencia ya la resuelve `units`.
    roster: [...vehicles]
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
      .slice(0, ROSTER_LIMIT)
      .map((vehicle) => ({ n: nameOf(vehicle), m: movementOf(vehicle) })),
    detail_truncated: ranked.length > DETAIL_LIMIT,
    roster_truncated: vehicles.length > ROSTER_LIMIT,
  };
}

export function focusUnitOnMap(query: string): boolean {
  const wanted = query.trim().toLowerCase();
  if (wanted.length === 0) return false;

  const vehicles = scopedVehicles(useVehiclesSocketStore.getState().vehicles);
  const match = vehicles.find((vehicle) =>
    [vehicle.vehicle?.name, vehicle.vehicle?.plates, vehicle.fullId, vehicle.id]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase() === wanted),
  );

  if (!match) return false;

  useVehiclesSocketStore.getState().setFocusedVehicleId(match.id);
  return true;
}
